import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MapProvider } from '../map/map-provider';
import { IdentifyProvider } from '../identify/identify-provider';
import { MeasureService } from './measure-service';
import { ProjectionService } from '../../../../shared/services/projection-service';
import { MeasureState } from './measure-state';
import { MeasureToolEnum } from './measure-tool-enum';
import { MeasureUnitEnum } from './measure-unit-enum';
import MapView from '@arcgis/core/views/MapView';
import Point from '@arcgis/core/geometry/Point';

@Injectable()
export class MeasureNotifier extends BaseNotifier<MeasureState> {
  private static readonly INITIAL: MeasureState = {
    activeTool: null,
    unit: MeasureUnitEnum.Meters,
    perimeterUnit: MeasureUnitEnum.Kilometers,
    isDrawing: false,
    results: [],
    locationResults: [],
    liveLabel: null,
    copyFeedback: null,
  };

  private identifyWasActive = false;

  constructor(ref: Ref) {
    super(ref, MeasureNotifier.INITIAL);

    this.measureSvc.setOnResult((id, rawValue, unit, label, perimeterRawValue) => {
      const state = this.snapshot();
      if (!state.activeTool || state.activeTool === MeasureToolEnum.Location) return;
      const t = state.activeTool;

      // Usar las unidades que el usuario tiene configuradas
      const customizedLabel = this.rebuildResultLabel({
        type: t,
        rawValue,
        perimeterRawValue
      } as any, state.unit, state.perimeterUnit);
      
      this.measureSvc.updateMapLabel(id, customizedLabel);

      const result: MeasureState.Result = {
        id,
        type: t,
        rawValue,
        unit: state.unit,
        perimeterUnit: state.perimeterUnit,
        label: customizedLabel,
        ...(perimeterRawValue != null ? { perimeterRawValue } : {})
      };

      this.updateState(s => ({
        ...s,
        isDrawing: false,
        results: [...s.results.filter(r => r.type !== t), result]
      }));
      this.deactivateTool();
    });

    this.measureSvc.setOnLiveUpdate(label => {
      this.updateState(s => ({ ...s, liveLabel: label }));
    });

  }

  private get mapProvider() { return this.ref.watch(MapProvider); }
  private get measureSvc() { return this.ref.watch(MeasureService); }
  private get projectionSvc() { return this.ref.watch(ProjectionService); }
  private get identifyProvider() { return this.ref.watch(IdentifyProvider); }

  get unitOptions(): { value: MeasureUnitEnum; label: string }[] {
    const pool = MeasureUnitEnum.isLinear(this.snapshot().unit) ? MeasureUnitEnum.LINEAR : MeasureUnitEnum.AREA;
    return pool.map(v => ({ value: v, label: MeasureUnitEnum.LABELS[v] }));
  }

  private formatLocationLabel(lat: number, lon: number): { label: string; wgs84Label: string; utmLabel: string; utmZone: string } {
    const wgs84Label = this.projectionSvc.toWgs84(lat, lon);
    const utmZone = this.projectionSvc.detectUtmZone(lon);
    const utmRaw = this.projectionSvc.toUtm(lat, lon, utmZone);
    const [eastStr, northStr] = utmRaw.split(/\s+/);
    const utmLabel = eastStr && northStr ? `${eastStr} E · ${northStr} N` : '—';
    const label = `Geográficas: ${wgs84Label}\nUTM (${utmZone}): ${utmLabel}`;
    return { label, wgs84Label, utmLabel, utmZone };
  }

  selectTool(tool: MeasureToolEnum | null): void {
    if (tool === null) {
      this.deactivateTool();
      return;
    }
    const current = this.snapshot().activeTool;
    if (current === tool) {
      this.deactivateTool();
      return;
    }
    if (!current) {
      this.identifyWasActive = this.identifyProvider.notifier.state().active;
    }
    this.identifyProvider.notifier.setEnabled(false);

    let unit = this.snapshot().unit;
    let perimeterUnit = this.snapshot().perimeterUnit;
    if (tool === MeasureToolEnum.Distance) {
      if (MeasureUnitEnum.isArea(unit)) unit = MeasureUnitEnum.Meters;
    } else if (tool === MeasureToolEnum.Area) {
      if (MeasureUnitEnum.isLinear(unit)) unit = MeasureUnitEnum.SquareMeters;
    }

    this.setState({
      activeTool: tool,
      unit,
      perimeterUnit,
      isDrawing: false,
      results: this.snapshot().results,
      locationResults: this.snapshot().locationResults,
      liveLabel: null,
      copyFeedback: null
    });

    const view = this.mapProvider.state().view;
    if (!view) return;
    if (!this.measureSvc.initialized) this.measureSvc.initialize(view as MapView);

    this.measureSvc.deactivate();
    // Sincronizar unidades con el servicio
    this.measureSvc.setUnit(unit);
    this.measureSvc.setPerimeterUnit(perimeterUnit);
  }

  startMeasurement(): void {
    const state = this.snapshot();
    const tool = state.activeTool;
    if (!tool) return;

    this.updateState(s => ({ ...s, isDrawing: true }));

    const view = this.mapProvider.state().view;
    if (!view) return;

    if (tool === MeasureToolEnum.Location) {
      this.measureSvc.removeLocationRecord();
      this.measureSvc.activateLocation((lat, lon) => {
        if (this.snapshot().activeTool !== MeasureToolEnum.Location) return;
        const { label, wgs84Label, utmLabel, utmZone } = this.formatLocationLabel(lat, lon);
        const result: MeasureState.LocationResult = { lat, lon, label, wgs84Label, utmLabel, utmZone };
        this.updateState(s => ({ ...s, locationResults: [result], isDrawing: false }));
        
        if (view) {
          const pt = new Point({ latitude: lat, longitude: lon, spatialReference: view.spatialReference });
          this.measureSvc.showLocationPoint(pt, label);
        }
        this.deactivateTool();
      });
    } else {
      this.measureSvc.selectTool(tool, state.unit, state.perimeterUnit);
    }
  }

  setUnit(unit: MeasureUnitEnum): void {
    this.updateState(s => ({ ...s, unit }));
    this.measureSvc.setUnit(unit);
  }

  setPerimeterUnit(unit: MeasureUnitEnum): void {
    this.measureSvc.setPerimeterUnit(unit);
    this.updateState(s => ({ ...s, perimeterUnit: unit }));
  }

  updateResultUnit(id: number, newUnit: MeasureUnitEnum, isPerimeter = false): void {
    const state = this.snapshot();
    const result = state.results.find(r => r.id === id);
    if (!result) return;

    let mainUnit = result.unit;
    let perimUnit = result.perimeterUnit ?? MeasureUnitEnum.Meters;

    if (result.type === MeasureToolEnum.Area) {
      if (isPerimeter) {
        perimUnit = newUnit;
      } else {
        mainUnit = newUnit;
      }
    } else {
      mainUnit = newUnit;
    }

    const label = this.rebuildResultLabel(result, mainUnit, perimUnit);
    this.measureSvc.updateMapLabel(id, label);
    this.updateState(s => ({
      ...s,
      results: s.results.map(r => r.id === id ? { ...r, unit: mainUnit, perimeterUnit: perimUnit, label } : r),
    }));
  }

  setCopyFeedback(feedback: string | null): void {
    this.updateState(s => ({ ...s, copyFeedback: feedback }));
  }

  private rebuildResultLabel(
    result: MeasureState.Result, mainUnit: MeasureUnitEnum, perimUnit: MeasureUnitEnum,
  ): string {
    if (result.type === MeasureToolEnum.Distance) {
      return this.measureSvc.formatResultValue(result.rawValue, mainUnit, MeasureToolEnum.Distance);
    } else {
      const areaLabel = this.measureSvc.formatResultValue(result.rawValue, mainUnit, MeasureToolEnum.Area);
      if (result.perimeterRawValue == null) return areaLabel;
      const perimLabel = this.measureSvc.formatResultValue(result.perimeterRawValue, perimUnit, MeasureToolEnum.Distance);
      return `Área: ${areaLabel}\nPerímetro: ${perimLabel}`;
    }
  }

  removeResult(id: number): void {
    this.measureSvc.removeRecord(id);
    this.updateState(s => ({ ...s, results: s.results.filter(r => r.id !== id) }));
  }

  removeLocationResult(): void {
    this.measureSvc.removeLocationRecord();
    this.measureSvc.deactivate();
    this.updateState(s => ({ ...s, locationResults: [] }));
  }

  clearAll(): void {
    this.measureSvc.clearAllRecords();
    this.updateState(s => ({ ...s, results: [], locationResults: [], activeTool: null }));
    this.measureSvc.deactivate();
    this.identifyProvider.notifier.setEnabled(this.identifyWasActive);
  }

  private deactivateTool(): void {
    this.updateState(s => ({ ...s, activeTool: null, isDrawing: false }));
    this.measureSvc.deactivate();
    this.identifyProvider.notifier.setEnabled(this.identifyWasActive);
  }
}
